/**
 * projectRunStream (RFC 0217: reify the run).
 *
 * Projects a run's log entries into the legacy production StreamChunk sequence
 * (src/types/stream-types.ts: content | tool | error | finish) that the harness
 * adapters and `handleStream` consume. This is the compatibility shim that lets
 * the worker runtime drive a run while the UI contract is preserved; it is the
 * only genuinely new surface in 0217's caller swap, and it has a deliberate
 * sunset (once the UI subscribes to the log directly via projectTranscript).
 *
 * It is a STREAMING projection: it subscribes to log changes and emits
 * incrementally as the inference/tool workers write entries, then ends when the
 * run reaches a terminal status. It diffs each assistant message's text so it
 * emits only newly-appended content (mirroring per-delta streaming).
 */

import { Automerge } from '@automerge/automerge-repo/slim'
import type { BranchLog } from '../../log/branch-log'
import { topoOrder } from '../../log/projection-order'
import type { Entry, EntryId, LogDoc } from '../../log/types'
import type { StreamChunk } from './stream-types'

/**
 * Coerce a decoded value to plain JSON. Tool arguments round-trip through
 * Automerge as ImmutableString-laden proxies (RFC 0215 on-doc shape); the legacy
 * StreamChunk `data` must be plain JSON for the UI, so normalize leaves here.
 */
function plainJson(v: unknown): unknown {
  if (v instanceof Automerge.ImmutableString)
    return (v as { val?: string }).val ?? String(v)
  if (Array.isArray(v)) return v.map(plainJson)
  if (v && typeof v === 'object') {
    const o: Record<string, unknown> = {}
    for (const [k, val] of Object.entries(v as Record<string, unknown>))
      o[k] = plainJson(val)
    return o
  }
  return v
}

function indexDoc(log: BranchLog): LogDoc {
  const entries: Record<EntryId, Entry> = {}
  for (const e of log.entries()) entries[e.id] = e
  return { schemaVersion: 1, entries, toolWorkers: {} }
}

/** Resolve a tool_use block's tool name by its id, across the run's assistants. */
function toolNameFor(ordered: Entry[], toolUseId: string): string {
  for (const e of ordered) {
    if (e.payload.type !== 'message') continue
    const block = e.payload.payload.toolUses.find((b) => b.id === toolUseId)
    if (block) return block.name
  }
  return 'tool'
}

export async function* projectRunStream(
  log: BranchLog,
  runId: EntryId,
): AsyncIterable<StreamChunk> {
  const emittedText = new Map<EntryId, number>() // assistant msg id -> chars emitted
  const emittedToolUse = new Set<string>() // tool_use block ids announced
  const emittedToolResult = new Set<EntryId>() // tool_result entry ids emitted
  let finished = false

  // Compute the chunks owed given current doc state; mutate the emitted-* sets.
  function drainPending(): StreamChunk[] {
    const doc = indexDoc(log)
    const ordered = topoOrder(doc)
    const out: StreamChunk[] = []
    let lastAssistantText = ''

    for (const e of ordered) {
      const p = e.payload
      if (p.type === 'message' && p.payload.runId === runId) {
        const m = p.payload
        if (m.role === 'assistant') {
          const text = m.text ?? ''
          const prev = emittedText.get(e.id) ?? 0
          if (text.length > prev) {
            out.push({ type: 'content', content: text.slice(prev) })
            emittedText.set(e.id, text.length)
          }
          if (m.status === 'completed') lastAssistantText = text
          for (const block of m.toolUses) {
            if (!emittedToolUse.has(block.id)) {
              emittedToolUse.add(block.id)
              out.push({
                type: 'tool',
                toolName: block.name,
                toolCallId: block.id,
                data: plainJson(block.arguments),
              })
            }
          }
        } else if (m.role === 'tool_result' && m.status === 'completed') {
          if (!emittedToolResult.has(e.id) && m.toolUseId) {
            emittedToolResult.add(e.id)
            if (m.isError) {
              out.push({ type: 'error', error: m.text ?? '' })
            } else {
              out.push({
                type: 'tool',
                toolName: toolNameFor(ordered, m.toolUseId),
                toolCallId: m.toolUseId,
                content: m.text ?? '',
              })
            }
          }
        }
      }
    }

    const run = doc.entries[runId]
    if (run?.payload.type === 'run') {
      const status = run.payload.payload.status
      if (status === 'completed') {
        finished = true
        out.push({ type: 'finish', finalOutput: lastAssistantText })
      } else if (status === 'cancelled') {
        // Terminal too. Without this, any consumer of StartedRun.stream waits
        // forever for a run the user already stopped.
        finished = true
        out.push({ type: 'finish', finalOutput: lastAssistantText })
      } else if (status === 'failed') {
        finished = true
        out.push({
          type: 'error',
          error: run.payload.payload.error ?? 'run failed',
        })
        out.push({ type: 'finish' })
      }
    }
    return out
  }

  // Drive the projection off log changes until the run is terminal.
  let notify: (() => void) | null = null
  const wake = () => notify?.()
  const unsub = log.onChange(wake)
  try {
    while (!finished) {
      for (const chunk of drainPending()) yield chunk
      if (finished) break
      await new Promise<void>((resolve) => {
        notify = resolve
      })
      notify = null
    }
  } finally {
    unsub()
  }
}
